<template>
  <PreAuthPage class="pinv" card-class="pinv__card">
    <section class="sh-card__head">
      <LogoTile :branding="branding" :alt="tenantName" :size="48" />
      <h1 class="sh-card__title">{{ heading }}</h1>
      <p class="sh-card__subtitle">{{ body }}</p>
      <p v-if="email" class="sh-whoami">{{ t("common.signedInAs") }} <b>{{ email }}</b></p>
    </section>

    <p v-if="session.programmeInviteBusy || phase === 'redeeming'" class="pinv__wait" role="status">
      {{ t("programmeInvite.working") }}
    </p>

    <form v-else-if="phase === 'needs-firm'" class="pinv__form" @submit.prevent="submitFirm">
      <v-text-field
        v-model="firmName"
        :label="t('programmeInvite.firmName')"
        variant="outlined"
        density="comfortable"
        autocomplete="organization"
        autofocus
        :disabled="session.programmeInviteBusy"
      />
      <v-btn type="submit" color="primary" size="large" block :disabled="!canSubmitFirm" :loading="session.programmeInviteBusy">
        {{ t("programmeInvite.firmSubmit") }}
      </v-btn>
    </form>

    <div v-else-if="phase === 'needs-org'" class="sh-rows">
      <p v-if="session.programmeInviteMessage === 'notMember'" class="sh-card__error" role="alert">
        <v-icon size="15">error_outline</v-icon>
        <span>{{ t("programmeInvite.notMember") }}</span>
      </p>
      <button
        v-for="org in session.accessibleOrgs"
        :key="org.id"
        type="button"
        class="sh-row"
        :disabled="session.programmeInviteBusy"
        @click="chooseOrg(org.id)"
      >
        <LogoTile :src="org.logo || org.icon || null" :alt="org.name" :size="26" />
        <span class="sh-row__label">{{ org.name }}</span>
        <v-icon size="16">chevron_right</v-icon>
      </button>
      <p v-if="!session.accessibleOrgs.length" class="pinv__wait">{{ t("programmeInvite.retryBody") }}</p>
    </div>

    <template v-else-if="phase === 'retryable'">
      <v-btn color="primary" size="large" block :loading="session.programmeInviteBusy" @click="retry">
        {{ t("programmeInvite.tryAgain") }}
      </v-btn>
    </template>

    <v-btn v-else class="pinv__signout" color="primary" variant="flat" block @click="session.signOut()">
      {{ t("programmeInvite.signOut") }}
    </v-btn>
  </PreAuthPage>
</template>

<script lang="ts">
/**
 * INS-37 — the screens a programme invite can land on after Auth0.
 * Portal invites never reach this view; they stay on the existing redeem.
 */
import { computed, defineComponent, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useSessionStore } from "@/stores/session";
import type { ProgrammeRedeemPhase } from "@/auth/programmeInvite";
import LogoTile from "@/shell/LogoTile.vue";
import PreAuthPage from "@/shell/PreAuthPage.vue";

const COPY: Record<ProgrammeRedeemPhase | "redeeming", { title: string; body: string }> = {
  redeeming: { title: "programmeInvite.workingTitle", body: "programmeInvite.working" },
  "needs-firm": { title: "programmeInvite.firmTitle", body: "programmeInvite.firmBody" },
  "needs-org": { title: "programmeInvite.orgTitle", body: "programmeInvite.orgBody" },
  "not-member": { title: "programmeInvite.orgTitle", body: "programmeInvite.orgBody" },
  success: { title: "programmeInvite.workingTitle", body: "programmeInvite.working" },
  "wrong-email": { title: "programmeInvite.wrongEmailTitle", body: "programmeInvite.wrongEmailBody" },
  revoked: { title: "programmeInvite.revokedTitle", body: "programmeInvite.revokedBody" },
  expired: { title: "programmeInvite.expiredTitle", body: "programmeInvite.expiredBody" },
  "already-used": { title: "programmeInvite.usedTitle", body: "programmeInvite.usedBody" },
  unknown: { title: "programmeInvite.unknownTitle", body: "programmeInvite.unknownBody" },
  retryable: { title: "programmeInvite.retryTitle", body: "programmeInvite.retryBody" },
};

export default defineComponent({
  name: "ProgrammeInvite",
  components: { LogoTile, PreAuthPage },
  setup() {
    const { t } = useI18n();
    const session = useSessionStore();
    const firmName = ref("");

    const phase = computed(() => session.programmeInvitePhase);
    const copy = computed(() => COPY[phase.value] ?? COPY.unknown);
    const canSubmitFirm = computed(() => firmName.value.trim().length > 1 && !session.programmeInviteBusy);

    onMounted(() => {
      void session.ensureProgrammeInviteRedeem();
    });

    function submitFirm(): void {
      if (!canSubmitFirm.value) return;
      void session.submitProgrammeInvite({ firmName: firmName.value.trim() });
    }

    function chooseOrg(organisationId: number): void {
      void session.submitProgrammeInvite({ organisationId });
    }

    function retry(): void {
      const firm = phase.value === "needs-firm" ? firmName.value.trim() : undefined;
      void session.submitProgrammeInvite(firm ? { firmName: firm } : undefined);
    }

    return {
      t,
      session,
      firmName,
      phase,
      canSubmitFirm,
      submitFirm,
      chooseOrg,
      retry,
      heading: computed(() => t(copy.value.title)),
      body: computed(() => t(copy.value.body)),
      tenantName: computed(() => session.tenant?.name || ""),
      branding: computed(() => session.tenant?.branding ?? null),
      email: computed(() => session.user?.email || null),
    };
  },
});
</script>

<style scoped>
.pinv__form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.pinv__wait {
  margin: 0;
  font-size: 0.85rem;
  color: var(--sh-text-60);
  text-align: center;
}
.pinv__signout {
  font-weight: 650;
}
</style>
